import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined"
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Popover from "@mui/material/Popover"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { formatDistanceToNow } from "date-fns"
import { useState } from "react"
import { Link as RouterLink } from "react-router-dom"

import Column from "../../../common/components/Column"
import { trimLongString } from "../../../common/utils"
import { getCallDispositionLabel } from "../calling/callDispositions"
import FeedbackClientReplySection, {
  FeedbackClientReplyHistory,
} from "../components/FeedbackClientReplySection"
import { buildLeadDetailsPath } from "../my-mls/leadDetailsNavigation"
import CallRecordingPlayer from "./CallRecordingPlayer"
import TalkTimeCapsule from "./TalkTimeCapsule"
import { hasCallRecording } from "./hasCallRecording"
import { ACTIVITY_TYPES } from "./utils"

const ellipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}

function ActivityEntityLink({ to, children }) {
  return (
    <Link
      component={RouterLink}
      to={to}
      underline="hover"
      onClick={(event) => event.stopPropagation()}
      sx={{
        color: "colors.brand600",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        whiteSpace: "nowrap",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      {children}
      <OpenInNewOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
    </Link>
  )
}

const RECORD_LAYOUT_SX = {
  display: "grid",
  gridTemplateColumns: "36px minmax(0, 1.1fr) minmax(0, 1.35fr) 168px",
  columnGap: 1.5,
  alignItems: "center",
  width: "100%",
  minWidth: 0,
}

const RECORD_ACTIONS_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 0.75,
  width: "100%",
  minHeight: 32,
  minWidth: 0,
}

const ACTIVITY_TYPE_ICONS = {
  [ACTIVITY_TYPES.call]: { Icon: PhoneOutlinedIcon, label: "Call activity" },
  [ACTIVITY_TYPES.sms]: { Icon: SmsOutlinedIcon, label: "SMS activity" },
  [ACTIVITY_TYPES.email]: { Icon: EmailOutlinedIcon, label: "Email activity" },
  [ACTIVITY_TYPES.note]: {
    Icon: DescriptionOutlinedIcon,
    label: "Note activity",
  },
  [ACTIVITY_TYPES.offer]: {
    Icon: LocalOfferOutlinedIcon,
    label: "Proposal activity",
  },
  [ACTIVITY_TYPES.file]: {
    Icon: AttachFileOutlinedIcon,
    label: "File activity",
  },
  [ACTIVITY_TYPES.task]: {
    Icon: AssignmentOutlinedIcon,
    label: "Task activity",
  },
  [ACTIVITY_TYPES.taskOpen]: {
    Icon: AssignmentOutlinedIcon,
    label: "Task activity",
  },
  [ACTIVITY_TYPES.taskCompleted]: {
    Icon: AssignmentOutlinedIcon,
    label: "Task activity",
  },
  [ACTIVITY_TYPES.meeting]: {
    Icon: CalendarTodayOutlinedIcon,
    label: "Meeting activity",
  },
  [ACTIVITY_TYPES.feedback]: {
    Icon: FeedbackOutlinedIcon,
    label: "Feedback activity",
  },
}

export function getActivityTypeIcon(activityType) {
  return (
    ACTIVITY_TYPE_ICONS[activityType] ||
    ACTIVITY_TYPE_ICONS[ACTIVITY_TYPES.note]
  )
}

export function getActivityLeadDetailsPath(lead) {
  const refId = lead?.ref_id
  if (!refId) {
    return null
  }
  if (lead.source === "uploaded" || lead.source === "mls") {
    return buildLeadDetailsPath(lead.source, refId)
  }
  return null
}

export function getActivityRecordPreview(activity) {
  if (activity?.activity_type === ACTIVITY_TYPES.call) {
    if (activity.disposition) {
      return getCallDispositionLabel(activity.disposition)
    }
    if (activity.notes) {
      return trimLongString(activity.notes, 70)
    }
  }
  if (activity?.detail) {
    return trimLongString(activity.detail, 70)
  }
  return ""
}

export default function ActivityRecordCard({
  activity,
  selected = false,
  onSelect,
  onFeedbackRepliesUpdated,
}) {
  const [recordingAnchor, setRecordingAnchor] = useState(null)
  const [clientReplies, setClientReplies] = useState(
    () => activity.client_replies ?? []
  )
  const { Icon, label: typeIconLabel } = getActivityTypeIcon(
    activity.activity_type
  )
  const performerName = activity.performed_by_name
  const whenLabel = activity.created
    ? `${formatDistanceToNow(new Date(activity.created))} ago`
    : ""
  const metaParts = [
    performerName ? `By ${performerName}` : null,
    activity.performed_by_badge || null,
    whenLabel || null,
  ].filter(Boolean)
  const preview = getActivityRecordPreview(activity)
  const candidateName = activity.candidate?.candidate_name
  const leadLabel = activity.lead
    ? [
        "My Lead",
        activity.lead.name ||
          activity.lead.phone ||
          activity.lead.ref_id ||
          "Unknown lead",
        activity.lead.source === "uploaded" ? "(Uploaded)" : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : ""
  const leadDetailsPath = getActivityLeadDetailsPath(activity.lead)
  const showTalkTime = activity.activity_type === ACTIVITY_TYPES.call
  const showRecording = hasCallRecording(activity)
  const isFeedback = activity.activity_type === ACTIVITY_TYPES.feedback

  function handleRepliesUpdated(replies) {
    setClientReplies(replies)
    onFeedbackRepliesUpdated?.(activity.id, replies)
  }

  return (
    <Box
      data-testid={`activity-record-${activity.id}`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onSelect?.(activity.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onSelect?.(activity.id)
        }
      }}
      sx={{
        px: 1.5,
        py: 1,
        minHeight: 64,
        borderRadius: "14px",
        border: "1px solid",
        borderColor: selected ? "colors.brand400" : "colors.gray200",
        backgroundColor: selected ? "colors.brand50" : "colors.white",
        cursor: "pointer",
      }}
    >
      <Box sx={RECORD_LAYOUT_SX}>
        <Box
          aria-label={typeIconLabel}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: selected ? "colors.brand100" : "colors.gray100",
            color: selected ? "colors.brand700" : "colors.gray700",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Column gap={0.15} sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: "colors.gray800",
              fontWeight: 700,
              lineHeight: 1.25,
              ...ellipsisSx,
            }}
          >
            {activity.title}
          </Typography>
          {metaParts.length > 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.3, ...ellipsisSx }}
            >
              {metaParts.join(" · ")}
            </Typography>
          ) : null}
        </Column>
        <Column gap={0.15} sx={{ minWidth: 0 }}>
          {preview ? (
            <Typography
              variant="body2"
              sx={{
                color: "colors.gray700",
                fontWeight: 600,
                lineHeight: 1.25,
                ...ellipsisSx,
              }}
            >
              {preview}
            </Typography>
          ) : null}
          {activity.candidate ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Candidate:{" "}
              <ActivityEntityLink to={`/candidates/${activity.candidate.id}`}>
                {candidateName}
              </ActivityEntityLink>
            </Typography>
          ) : leadLabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              {leadDetailsPath ? (
                <ActivityEntityLink to={leadDetailsPath}>
                  {leadLabel}
                </ActivityEntityLink>
              ) : (
                leadLabel
              )}
            </Typography>
          ) : null}
        </Column>
        <Box data-testid="activity-record-actions" sx={RECORD_ACTIONS_SX}>
          {isFeedback ? (
            <FeedbackClientReplySection
              compact
              showHistory={false}
              feedbackEventId={activity.source_id}
              feedbackType={activity.feedback_type}
              feedbackComments={activity.feedback_comments ?? activity.detail}
              candidateName={candidateName}
              clientReplies={clientReplies}
              onRepliesUpdated={handleRepliesUpdated}
            />
          ) : null}
          {showRecording ? (
            <>
              <IconButton
                size="small"
                aria-label="Play recording"
                onClick={(event) => {
                  event.stopPropagation()
                  setRecordingAnchor(event.currentTarget)
                }}
                sx={{ color: "colors.brand600" }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
              <Popover
                open={Boolean(recordingAnchor)}
                anchorEl={recordingAnchor}
                onClose={() => setRecordingAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Box sx={{ p: 1 }}>
                  <CallRecordingPlayer
                    recordingFile={activity.recording_file}
                  />
                </Box>
              </Popover>
            </>
          ) : null}
          {showTalkTime ? (
            <TalkTimeCapsule talkTime={activity.talk_time} sx={{ ml: 0 }} />
          ) : null}
        </Box>
      </Box>
      {isFeedback && clientReplies.length > 0 ? (
        <Box sx={{ pl: 6.5, pt: 0.75 }} onClick={(e) => e.stopPropagation()}>
          <FeedbackClientReplyHistory replies={clientReplies} />
        </Box>
      ) : null}
    </Box>
  )
}

export function ActivityRecordCardSkeleton() {
  return (
    <Column gap={1}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            px: 1.5,
            py: 1,
            minHeight: 64,
            borderRadius: "14px",
            border: "1px solid",
            borderColor: "colors.gray200",
          }}
        >
          <Box sx={RECORD_LAYOUT_SX}>
            <Skeleton variant="circular" width={36} height={36} />
            <Column gap={0.4} sx={{ minWidth: 0 }}>
              <Skeleton variant="text" width="80%" height={18} />
              <Skeleton variant="text" width="60%" height={14} />
            </Column>
            <Column gap={0.4} sx={{ minWidth: 0 }}>
              <Skeleton variant="text" width="70%" height={18} />
              <Skeleton variant="text" width="50%" height={14} />
            </Column>
            <Box sx={RECORD_ACTIONS_SX}>
              <Skeleton variant="rounded" width={108} height={24} />
            </Box>
          </Box>
        </Box>
      ))}
    </Column>
  )
}
